import { useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

const SQUEEZE_MS = 300;
const HEIGHT_RATIO = 0.85;

const ActiveIndicator = ({ containerRef }) => {
  const location = useLocation();
  const [pos, setPos] = useState(null);
  const [squeezed, setSqueezed] = useState(false);
  const squeezeTimeout = useRef();
  // Becomes true only after the indicator has painted once at its resting
  // position, so the very first appearance never animates in from (0, 0).
  const transitionsEnabled = useRef(false);

  const measure = () => {
    const container = containerRef.current;
    if (!container) return;
    const active = container.querySelector(".active-menu-item");
    if (!active) {
      setPos(null);
      return;
    }
    const containerBox = container.getBoundingClientRect();
    const activeBox = active.getBoundingClientRect();
    const inset = (activeBox.height * (1 - HEIGHT_RATIO)) / 2;
    setPos({
      x: activeBox.left - containerBox.left,
      y: activeBox.top - containerBox.top + inset,
      height: activeBox.height - inset * 2,
    });
  };

  useLayoutEffect(() => {
    measure();
    if (transitionsEnabled.current) {
      setSqueezed(true);
      clearTimeout(squeezeTimeout.current);
      squeezeTimeout.current = setTimeout(() => setSqueezed(false), SQUEEZE_MS);
    }
    return () => clearTimeout(squeezeTimeout.current);
  }, [location.pathname]);

  useLayoutEffect(() => {
    if (pos) transitionsEnabled.current = true;
  }, [pos]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    // Menu items are gated behind role data fetched asynchronously, so on
    // first mount the list can still be empty when we first measure it —
    // watch for it filling in (or any later collapse/expand reflow) and
    // re-measure rather than relying solely on route changes.
    const resizeObserver = new ResizeObserver(() => measure());
    resizeObserver.observe(container);
    const mutationObserver = new MutationObserver(() => measure());
    mutationObserver.observe(container, { childList: true, subtree: true });
    window.addEventListener("resize", measure);
    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  // Belt-and-suspenders: whatever the exact async timing of the role-gated
  // data that populates the list turns out to be, keep re-checking for a
  // couple seconds after mount until something is actually found.
  useLayoutEffect(() => {
    if (pos) return;
    let attempts = 0;
    const id = setInterval(() => {
      attempts += 1;
      measure();
      if (attempts >= 30) clearInterval(id);
    }, 150);
    return () => clearInterval(id);
  }, [pos]);

  if (!pos) return null;

  return (
    <div
      className="nav-active-indicator"
      style={{
        height: pos.height,
        transform: `translate(${pos.x}px, ${pos.y}px) scaleY(${squeezed ? 0.7 : 1})`,
        transition: transitionsEnabled.current
          ? "transform 0.45s cubic-bezier(0.34, 1.56, 0.64, 1), height 0.45s ease"
          : "none",
      }}
    />
  );
};

export default ActiveIndicator;
